import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { BookingCalendar } from '../home/components/BookingCalendar';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';
import ProductNav from '../../components/feature/ProductNav';
import WholeLifeCalculator from './components/WholeLifeCalculator';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function WholeLifePage() {
  const [showBooking, setShowBooking] = useState(false);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/whole-life#webpage`,
    url: `${SITE_URL}/whole-life`,
    name: 'Whole Life Insurance Indianapolis - Permanent Coverage with Cash Value',
    description: 'Whole life insurance in Indianapolis with guaranteed cash value growth, lifetime coverage, and tax-free benefits. Get a free quote from Benjamin Wright.',
    inLanguage: 'en-US',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    datePublished: '2024-01-15T00:00:00-05:00',
    dateModified: new Date().toISOString(),
    breadcrumb: { '@id': `${SITE_URL}/whole-life#breadcrumb` },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '#how-whole-life-works-heading', '#why-choose-heading']
    }
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${SITE_URL}/whole-life#service`,
    name: 'Whole Life Insurance',
    description: 'Permanent life insurance with guaranteed cash value growth, lifetime coverage, and tax-free benefits for Indianapolis families.',
    provider: { '@id': `${SITE_URL}/#organization` },
    areaServed: {
      '@type': 'State',
      name: 'Indiana'
    },
    serviceType: 'Whole Life Insurance',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '94',
      bestRating: '5',
      worstRating: '1'
    },
    review: [
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Robert Patterson'
        },
        datePublished: '2024-11-10',
        reviewBody: 'I wanted permanent coverage with cash value growth for estate planning. Benjamin explained whole life vs IUL options thoroughly and helped me choose a participating policy from Northwestern Mutual. After 8 years, my cash value has grown exactly as projected. Excellent long-term planning partner.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Lisa Anderson'
        },
        datePublished: '2024-10-05',
        reviewBody: 'As a Carmel business owner, I needed key person insurance for my partner and myself. Hoosier Life Insurance structured whole life policies that protect our business and build corporate assets. The cash value has already helped us secure a business loan. Highly recommend for business planning.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'James Mitchell'
        },
        datePublished: '2024-09-18',
        reviewBody: 'I was comparing whole life vs term life and Benjamin took the time to show me real illustrations from multiple carriers. The guaranteed cash value growth and lifetime coverage made whole life the right choice for my family. Five years in and very happy with the decision.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Patricia Hughes'
        },
        datePublished: '2024-08-22',
        reviewBody: 'We needed whole life insurance for estate equalization - leaving our farm to one child and equal value to the other. Benjamin designed a policy that perfectly solves this problem. The premiums are fixed for life and the death benefit is guaranteed. Peace of mind achieved.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Thomas Reynolds'
        },
        datePublished: '2024-07-14',
        reviewBody: 'I wanted a policy that would never expire and could supplement my retirement income. Benjamin showed me how whole life cash value grows tax-deferred and can be accessed through policy loans. After 12 years, I have over $180K in cash value. Best financial decision I ever made.',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        }
      }
    ]
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is whole life insurance and how is it different from term?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Whole life insurance provides lifetime coverage with a guaranteed death benefit and builds cash value over time. Unlike term insurance which expires after 10-30 years, whole life never expires as long as premiums are paid. Whole life also accumulates tax-deferred cash value you can borrow against, while term life has no cash value.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does the cash value component work in whole life insurance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Part of your premium builds cash value that grows at a guaranteed rate (typically 2-4% annually). The cash value is yours to access through policy loans or withdrawals. After 10–15 years, many policies have substantial cash value. For example, a $500/month premium might build $42,000 in cash value by year 10 and $115,000 by year 20.',
        },
      },
      {
        '@type': 'Question',
        name: 'Why is whole life insurance more expensive than term?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Whole life costs more because it provides lifetime coverage (not just 10-30 years), builds cash value that you can access, and includes guaranteed premiums that never increase. You are essentially pre-paying for coverage in your later years when insurance becomes more expensive. Term life only covers you for a specific period with no cash value.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I borrow money from my whole life insurance policy?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Once your policy builds cash value (typically after 2-3 years), you can borrow against it at low interest rates (usually 5-8%). Policy loans require no credit check, no approval process, and have flexible repayment terms. The loan is not taxable, and your cash value continues to grow even while borrowed. If unpaid, the loan balance is deducted from the death benefit.',
        },
      },
      {
        '@type': 'Question',
        name: 'What are dividends and which carriers pay them?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Dividends are annual payments made by mutual insurance companies (like Northwestern Mutual, MassMutual, New York Life, and Penn Mutual) to participating whole life policyholders. While not guaranteed, these carriers have paid dividends consistently for over 100 years. You can use dividends to: buy additional coverage, increase cash value, receive as cash, or reduce premiums. Dividends can boost your cash value growth by an additional 1-2% annually.',
        },
      },
      {
        '@type': 'Question',
        name: 'Should I choose whole life or indexed universal life (IUL)?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Choose whole life if you want guaranteed cash value growth, fixed premiums for life, and zero market risk—ideal for conservative investors and estate planning. Choose IUL if you are under 50, comfortable with market-linked returns (with a 0% floor and 10-12% cap), and want premium flexibility. Whole life is simpler and more predictable; IUL has higher growth potential but requires active management and has lower guaranteed minimums.',
        },
      },
      {
        '@type': 'Question',
        name: 'How can business owners use whole life insurance?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Indianapolis business owners use whole life for: Key person insurance (protect against loss of critical employees), buy-sell agreements (fund business succession with guaranteed liquidity), executive bonus plans (attract top talent with tax-advantaged benefits), and business loan collateral (use cash value to secure financing without personal guarantees). The death benefit is tax-free to the business, and cash value builds as a corporate asset.',
        },
      },
      {
        '@type': 'Question',
        name: 'Are whole life insurance benefits taxed in Indiana?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No. Indiana does not tax life insurance death benefits or cash value growth. Death benefits pass to beneficiaries 100% income tax-free (federal and state). Cash value grows tax-deferred, and policy loans are not taxable. Indiana also has no state estate tax. This makes whole life one of the most tax-efficient wealth-building tools available to Indianapolis residents. For estates over $13.61 million (2024 federal limit), an Irrevocable Life Insurance Trust (ILIT) can remove the policy from your taxable estate.',
        },
      },
    ],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Set page title — under 60 chars
    document.title = 'Whole Life Insurance Indianapolis | Hoosier Life';

    // Helper function to set or create meta tags
    const setOrCreate = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set meta tags — description under 160 chars
    setOrCreate('description', 'Whole life insurance Indianapolis — lifetime coverage that builds guaranteed cash value. Borrow tax-free. A-rated carriers. Call (317) 648-2983.');
    setOrCreate('keywords', 'whole life insurance Indiana, permanent life insurance, cash value life insurance, guaranteed coverage Indiana');

    // Open Graph tags — use working readdy.ai image URL
    setOrCreate('og:title', 'Whole Life Insurance Indianapolis | Hoosier Life');
    setOrCreate('og:description', 'Whole life insurance Indianapolis — lifetime coverage that builds guaranteed cash value. Borrow tax-free. A-rated carriers. Call (317) 648-2983.');
    setOrCreate('og:url', 'https://hoosierlifeinsurance.com/whole-life');
    setOrCreate('og:type', 'website');
    setOrCreate('og:site_name', 'Hoosier Life Insurance');
    setOrCreate('og:image', 'https://public.readdy.ai/ai/img_res/a88b52ee546628e437ec7d97ead797fc.jpg');
    setOrCreate('og:image:width', '1200');
    setOrCreate('og:image:height', '630');

    // Twitter Card tags
    setOrCreate('twitter:card', 'summary_large_image');
    setOrCreate('twitter:title', 'Whole Life Insurance Indianapolis | Hoosier Life');
    setOrCreate('twitter:description', 'Whole life insurance Indianapolis — lifetime coverage that builds guaranteed cash value. Borrow tax-free. Call (317) 648-2983.');
    setOrCreate('twitter:image', 'https://readdy.ai/api/search-image?query=happy%20multi-generational%20Indianapolis%20family%20grandparents%20parents%20and%20grandchildren%20gathered%20in%20a%20bright%20warm%20modern%20American%20living%20room%20representing%20whole%20life%20insurance%20legacy%20planning%20and%20wealth%20transfer%20professional%20lifestyle%20photography%20warm%20natural%20light%20cream%20and%20beige%20tones&width=1200&height=630&seq=og-whole-life-v1&orientation=landscape');

    setOrCreate('last-modified', new Date().toISOString());

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${SITE_URL}/whole-life`);

    // Breadcrumb Schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': 'https://hoosierlifeinsurance.com/whole-life#breadcrumb',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://hoosierlifeinsurance.com/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Whole Life Insurance',
          item: 'https://hoosierlifeinsurance.com/whole-life'
        }
      ]
    };

    // InsuranceProduct Schema
    const insuranceProductSchema = {
      '@context': 'https://schema.org',
      '@type': 'InsuranceProduct',
      '@id': `${SITE_URL}/whole-life#insurance-product`,
      name: 'Whole Life Insurance',
      description: 'Permanent whole life insurance with guaranteed lifetime coverage, fixed premiums that never increase, and tax-deferred cash value growth. Available to Indiana individuals, families, and seniors seeking lifelong protection and legacy planning.',
      url: `${SITE_URL}/whole-life`,
      category: 'Life Insurance',
      brand: { '@type': 'Brand', name: 'Hoosier Life Insurance' },
      provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization` },
      isRelatedTo: { '@type': 'Organization', '@id': `${SITE_URL}/#business` },
      areaServed: { '@type': 'State', name: 'Indiana' },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'USD',
          description: 'Starting from $50/month depending on age, health, and coverage amount',
        },
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/whole-life`,
      },
    };

    const scriptEls: HTMLScriptElement[] = [];
    const schemasList = [webPageSchema, serviceSchema, faqSchema, breadcrumbSchema, insuranceProductSchema];
    schemasList.forEach((schema, i) => {
      const el = document.createElement('script');
      el.id = `schema-whole-life-${i}`;
      el.type = 'application/ld+json';
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      scriptEls.push(el);
    });

    return () => {
      scriptEls.forEach(el => { try { document.head.removeChild(el); } catch (e) { /* ignore */ } });
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      
      {/* Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="bg-[#1A3A52] border-b border-[#1A3A52]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm">
          <a href="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
            <i className="ri-home-4-line text-base"></i>
            <span>Home</span>
          </a>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <span className="text-[#D97642] font-semibold">Whole Life Insurance</span>
        </div>
      </nav>

      {/* Product Navigation */}
      <ProductNav />
      
      {/* Star Rating Display */}
      <StarRatingDisplay rating={4.9} reviewCount={94} productName="Whole Life Insurance Indianapolis" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235]">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D97642]/8 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#1A3A52]/20 rounded-full blur-3xl"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D97642]/20 rounded-full mb-6">
                <i className="ri-shield-check-line text-[#D97642]"></i>
                <span className="text-sm font-medium text-[#F4A97A]">Lifetime Protection + Cash Value</span>
              </div>
              <h1 id="hero-heading" className="text-5xl font-bold text-white mb-6 leading-tight">
                Whole Life Insurance — Permanent Coverage &amp; Cash Value, Indianapolis
              </h1>
              <p id="hero-description" className="text-xl text-white/80 mb-8 leading-relaxed">
                Whole life insurance covers you for your <strong>entire life</strong> — it never expires. While it protects your family, it also builds real savings you can borrow from, tax-free. Your monthly payment stays the same forever. Most Indianapolis families use it to build wealth AND leave something behind for their kids.
              </p>
              {/* Trust badges matching homepage pattern */}
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: 'ri-award-line', label: 'A-Rated Carriers' },
                  { icon: 'ri-lock-line', label: '100% Private & Secure' },
                  { icon: 'ri-map-pin-2-line', label: 'Local Indianapolis Agent' },
                  { icon: 'ri-infinity-line', label: 'Guaranteed Cash Value' },
                ].map((badge) => (
                  <div key={badge.label} className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm text-white/90">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${badge.icon} text-[#D97642] text-sm`}></i>
                    </div>
                    <span>{badge.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBooking(true)}
                  className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Get Your Quote
                </button>
                <a
                  href="/term-life"
                  className="px-8 py-4 bg-white/20 text-white rounded-lg font-semibold hover:bg-white/30 transition-all border-2 border-white/40 whitespace-nowrap"
                >
                  Compare Term Life
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20multi-generational%20family%20gathered%20in%20a%20bright%20modern%20living%20room%20grandparents%20in%20their%2060s%20parents%20in%20their%2030s%20and%20two%20young%20children%20all%20smiling%20warmly%20together%20soft%20natural%20window%20light%20neutral%20home%20decor%20representing%20lifetime%20family%20protection%20and%20legacy%20planning%20professional%20lifestyle%20photography%20warm%20tones&width=600&height=400&seq=wholelife-hero-family-2025d&orientation=landscape"
                  alt="Whole Life Insurance Indianapolis"
                  title="Whole Life Insurance for Indianapolis Families — Lifetime Coverage with Guaranteed Cash Value"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width={600}
                  height={400}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#D97642]/10 rounded-full flex items-center justify-center">
                    <i className="ri-line-chart-line text-2xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">$250K+</div>
                    <div className="text-sm text-gray-600">Avg Cash Value at Age 65</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Takeaways — AI SEO Signal */}
      <section className="py-8 bg-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-white font-bold text-lg mb-3">Key Takeaways: Whole Life Insurance in Indiana</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Whole life insurance NEVER expires — your family is covered as long as you pay your premium, forever',
                  'Part of every payment builds real savings — a "cash value" you can borrow from anytime, no credit check needed',
                  'After about 20 years, your savings account inside the policy can equal what you\'ve paid in total premiums',
                  'Policy loans are totally tax-free — use the money for emergencies, college, retirement, anything you want',
                  'Indiana has no state estate tax — your family gets every dollar of the death benefit, 100% income tax-free',
                  'Best for estate planning, business succession, or anyone who wants guaranteed protection for life',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/90 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-white/50 text-xs">Last reviewed March 2025 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* How Whole Life Works in Indiana */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="how-whole-life-works-heading" className="text-4xl font-bold text-gray-900 mb-4">How Whole Life Insurance Works in Indiana</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Think of it this way: term life is renting protection. Whole life is buying it — and it builds equity you can actually use while you&apos;re still alive.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-14 h-14 bg-[#D97642] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Premium Allocation</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Every premium payment is split into two parts: one portion covers your death benefit protection, while the other builds cash value in a tax-deferred account.
              </p>
              <div className="bg-[#F9F7F4] p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Death Benefit Coverage</span>
                  <span className="text-sm font-bold text-[#D97642]">40%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                  <div className="bg-[#D97642] h-2 rounded-full" style={{ width: '40%' }}></div>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Cash Value Growth</span>
                  <span className="text-sm font-bold text-[#1A3A52]">60%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#1A3A52] h-2 rounded-full" style={{ width: '60%' }}></div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-14 h-14 bg-[#1A3A52] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Guaranteed Growth</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Your cash value grows at a guaranteed rate (typically 2-4% annually) set by the insurance company. Many policies also pay annual dividends from participating carriers.
              </p>
              <div className="bg-[#F9F7F4] p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-2">Example: $500/month premium</div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700">Year 10 Cash Value:</span>
                    <span className="font-bold text-gray-900">$42,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700">Year 20 Cash Value:</span>
                    <span className="font-bold text-gray-900">$115,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-700">Year 30 Cash Value:</span>
                    <span className="font-bold text-[#D97642]">$225,000</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-14 h-14 bg-[#D97642] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-hand-coin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Access Your Cash Value</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                You can borrow against your cash value at low interest rates (typically 5-8%) or make withdrawals. The money is yours to use for emergencies, college tuition, or retirement income.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#D97642] mt-1"></i>
                  <span className="text-sm text-gray-700">No credit check required for policy loans</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#D97642] mt-1"></i>
                  <span className="text-sm text-gray-700">Tax-free withdrawals up to basis amount</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-[#D97642] mt-1"></i>
                  <span className="text-sm text-gray-700">Flexible repayment terms</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-14 h-14 bg-[#1A3A52] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-gift-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Dividend-Paying Carriers</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Top mutual insurance companies (like Northwestern Mutual, MassMutual, and New York Life) pay annual dividends to policyholders, which can significantly boost your cash value growth.
              </p>
              <div className="bg-[#F9F7F4] p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-3">Dividend Options:</div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#D97642]">•</span>
                    <span>Reinvest to buy additional coverage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#D97642]">•</span>
                    <span>Add to cash value accumulation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#D97642]">•</span>
                    <span>Receive as cash payment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#D97642]">•</span>
                    <span>Reduce premium payments</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F] p-8 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Indiana Advantage: No State Tax on Cash Value Growth</h3>
            <p className="text-white/80 max-w-3xl mx-auto">
              Indiana does not tax the cash value growth inside your whole life policy, and death benefits pass to beneficiaries 100% income tax-free. This makes whole life insurance one of the most tax-efficient wealth-building tools available to Indianapolis residents.
            </p>
          </div>
        </div>
      </section>

      {/* Whole Life for Business Owners in Indianapolis */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D97642]/20 rounded-full mb-4">
              <i className="ri-briefcase-line text-[#D97642]"></i>
              <span className="text-sm font-medium text-[#D97642]">Business Planning</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Whole Life Insurance for Business Owners in Indianapolis</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect your business, fund succession plans, and build tax-advantaged wealth with whole life insurance strategies designed for Indianapolis entrepreneurs.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#D97642]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-star-line text-2xl text-[#D97642]"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Key Person Insurance</h3>
                  <p className="text-gray-600">Protect your business from financial loss if a critical employee or owner dies</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="font-semibold text-gray-900 mb-2">How It Works:</div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Your business owns the policy on a key person (CEO, top salesperson, technical expert). If they pass away, the death benefit provides immediate cash to cover lost revenue, hire a replacement, and maintain operations during the transition.
                  </p>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg border border-gray-100">
                  <div className="font-semibold text-gray-900 mb-2">Tax Benefits:</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                      <span>Death benefit received tax-free by the business</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                      <span>Cash value can be used as collateral for business loans</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                      <span>Builds corporate asset on balance sheet</span>
                    </li>
                  </ul>
                </div>
                <div className="text-sm text-gray-600 italic">
                  <strong>Indianapolis Example:</strong> A manufacturing company in <a href="/service-areas/fishers" className="text-[#D97642] hover:underline">Fishers</a> insures their lead engineer for $1M. When he unexpectedly passes, they use the death benefit to cover 18 months of lost productivity and recruiter fees to find a replacement.
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-swap-line text-2xl text-[#1A3A52]"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Buy-Sell Agreements</h3>
                  <p className="text-gray-600">Fund business succession plans with guaranteed liquidity</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="font-semibold text-gray-900 mb-2">How It Works:</div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Business partners purchase whole life policies on each other. If one partner dies, the death benefit provides immediate cash to buy out the deceased partner's share from their family—avoiding forced liquidation or unwanted heirs becoming co-owners.
                  </p>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg border border-gray-100">
                  <div className="font-semibold text-gray-900 mb-2">Why Whole Life vs. Term:</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#1A3A52] mt-0.5"></i>
                      <span>Guaranteed coverage for life (no expiration risk)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#1A3A52] mt-0.5"></i>
                      <span>Fixed premiums that never increase</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#1A3A52] mt-0.5"></i>
                      <span>Cash value can fund early buyout if partner retires</span>
                    </li>
                  </ul>
                </div>
                <div className="text-sm text-gray-600 italic">
                  <strong>Indianapolis Example:</strong> Two partners in a <a href="/service-areas/carmel" className="text-[#1A3A52] hover:underline">Carmel</a> accounting firm each carry $750K whole life policies. When one partner retires at 62, they use the accumulated cash value ($180K) as part of the buyout payment.
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#D97642]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-[#D97642]"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Executive Bonus Plans</h3>
                  <p className="text-gray-600">Attract and retain top talent with tax-advantaged benefits</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="font-semibold text-gray-900 mb-2">How It Works:</div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    The business pays premiums on a whole life policy owned by the executive. The premium is tax-deductible to the business as compensation, and the executive owns the policy and its cash value outright.
                  </p>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg border border-gray-100">
                  <div className="font-semibold text-gray-900 mb-2">Benefits for Executives:</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                      <span>Builds personal wealth outside of salary</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                      <span>Can borrow against cash value for personal use</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                      <span>Portable benefit if they leave the company</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-bank-line text-2xl text-[#1A3A52]"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Business Loan Collateral</h3>
                  <p className="text-gray-600">Use cash value to secure financing without personal guarantees</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="font-semibold text-gray-900 mb-2">How It Works:</div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Banks and lenders accept whole life cash value as collateral for business loans. This allows you to access capital without pledging business assets or personal property.
                  </p>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg border border-gray-100">
                  <div className="font-semibold text-gray-900 mb-2">Advantages:</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#1A3A52] mt-0.5"></i>
                      <span>Lower interest rates than unsecured loans</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#1A3A52] mt-0.5"></i>
                      <span>Cash value continues to grow even while pledged</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#1A3A52] mt-0.5"></i>
                      <span>Protects personal credit and home equity</span>
                    </li>
                  </ul>
                </div>
                <div className="text-sm text-gray-600 italic">
                  <strong>Indianapolis Example:</strong> A restaurant owner in <a href="/service-areas/broad-ripple" className="text-[#1A3A52] hover:underline">Broad Ripple</a> uses $85K in policy cash value as collateral to secure a $200K expansion loan at 6.5% interest instead of 11% unsecured.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F] p-8 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Schedule a Business Planning Consultation</h3>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto leading-relaxed">
              Our Indianapolis team specializes in business succession planning and executive benefits. Let's design a strategy that protects your company and builds wealth.
            </p>
            <button
              onClick={() => setShowBooking(true)}
              className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
            >
              Book Your Strategy Session
            </button>
          </div>
        </div>
      </section>

      {/* IUL vs Whole Life Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Indexed Universal Life vs. Whole Life Insurance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Both offer permanent coverage and cash value growth, but they work very differently. Here's an honest comparison to help you choose the right policy for your Indianapolis family.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl border-2 border-[#D97642]/30 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-[#D97642] rounded-xl flex items-center justify-center">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Whole Life</h3>
              </div>
              
              <div className="space-y-6">
                <div>
                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-line-chart-line text-[#D97642]"></i>
                    Cash Value Growth
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Guaranteed</strong> growth rate (typically 2-4% annually) plus potential dividends from participating carriers. Your cash value is contractually guaranteed to reach specific amounts at specific ages.
                  </p>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-money-dollar-circle-line text-[#D97642]"></i>
                    Premium Structure
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Fixed for life.</strong> Your premium never changes, making it easy to budget. You know exactly what you'll pay every year until age 100.
                  </p>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-shield-line text-[#D97642]"></i>
                    Risk Level
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Zero market risk.</strong> Your cash value is not affected by stock market crashes or economic downturns. Conservative and predictable.
                  </p>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-user-line text-[#D97642]"></i>
                    Best For
                  </div>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-[#D97642]">•</span>
                      <span>Conservative investors who want guarantees</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D97642]">•</span>
                      <span>Estate planning and wealth transfer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D97642]">•</span>
                      <span>Business succession planning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#D97642]">•</span>
                      <span>People who want predictable premiums</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-2">Example: $500/month premium</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Year 20 Cash Value:</span>
                      <span className="font-bold text-[#D97642]">$115,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Guaranteed Minimum:</span>
                      <span className="font-bold text-gray-900">$110,000</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border-2 border-[#1A3A52]/30 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-[#1A3A52] rounded-xl flex items-center justify-center">
                  <i className="ri-stock-line text-3xl text-white"></i>
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Indexed Universal Life (IUL)</h3>
              </div>
              
              <div className="space-y-6">
                <div>
                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-line-chart-line text-[#1A3A52]"></i>
                    Cash Value Growth
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Tied to stock market index</strong> (usually S&amp;P 500) with a cap (typically 10-12%) and floor (usually 0-1%). Higher growth potential but not guaranteed. Can earn 0% in down years.
                  </p>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-money-dollar-circle-line text-[#1A3A52]"></i>
                    Premium Structure
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Flexible premiums.</strong> You can increase, decrease, or skip payments (as long as cash value covers policy costs). Requires active management to avoid lapse.
                  </p>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-alert-line text-[#1A3A52]"></i>
                    Risk Level
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Moderate risk.</strong> You won't lose money in market crashes (0% floor), but you could earn 0% for multiple years. Policy can lapse if cash value runs out.
                  </p>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-user-line text-[#1A3A52]"></i>
                    Best For
                  </div>
                  <ul className="space-y-1 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-[#1A3A52]">•</span>
                      <span>Younger buyers (under 45) with time to ride out volatility</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#1A3A52]">•</span>
                      <span>People comfortable with market-linked returns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#1A3A52]">•</span>
                      <span>Those who want premium flexibility</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#1A3A52]">•</span>
                      <span>Supplemental retirement income planning</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-2">Example: $500/month premium</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Year 20 Cash Value (projected):</span>
                      <span className="font-bold text-[#1A3A52]">$145,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Guaranteed Minimum:</span>
                      <span className="font-bold text-gray-900">$65,000</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#F9F7F4] p-8 rounded-2xl border border-gray-200 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Side-by-Side Comparison</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Feature</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#D97642]">Whole Life</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#1A3A52]">IUL</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium text-gray-900">Guaranteed Cash Value</td>
                    <td className="text-center py-3 px-4"><i className="ri-check-line text-2xl text-[#D97642]"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-line text-2xl text-gray-400"></i></td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-white">
                    <td className="py-3 px-4 font-medium text-gray-900">Fixed Premiums</td>
                    <td className="text-center py-3 px-4"><i className="ri-check-line text-2xl text-[#D97642]"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-line text-2xl text-gray-400"></i></td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium text-gray-900">Higher Growth Potential</td>
                    <td className="text-center py-3 px-4"><i className="ri-close-line text-2xl text-gray-400"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-check-line text-2xl text-[#1A3A52]"></i></td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-white">
                    <td className="py-3 px-4 font-medium text-gray-900">Premium Flexibility</td>
                    <td className="text-center py-3 px-4"><i className="ri-close-line text-2xl text-gray-400"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-check-line text-2xl text-[#1A3A52]"></i></td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-3 px-4 font-medium text-gray-900">Dividend Payments</td>
                    <td className="text-center py-3 px-4"><i className="ri-check-line text-2xl text-[#D97642]"></i></td>
                    <td className="text-center py-3 px-4"><i className="ri-close-line text-2xl text-gray-400"></i></td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-white">
                    <td className="py-3 px-4 font-medium text-gray-900">Complexity</td>
                    <td className="text-center py-3 px-4 text-[#D97642] font-medium">Simple</td>
                    <td className="text-center py-3 px-4 text-[#1A3A52] font-medium">Complex</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-900">Best for Ages</td>
                    <td className="text-center py-3 px-4 text-gray-700">All ages</td>
                    <td className="text-center py-3 px-4 text-gray-700">Under 50</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-[#F9F7F4] p-6 rounded-lg">
            <div className="flex items-start gap-3">
              <i className="ri-lightbulb-line text-2xl text-[#1A3A52] mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Our Honest Recommendation</h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>Choose Whole Life if:</strong> You want guarantees, predictable premiums, and zero market risk. It's the safer, more conservative choice for estate planning and business succession.
                </p>
                <p className="text-sm text-gray-700 leading-relaxed mt-2">
                  <strong>Choose IUL if:</strong> You're under 45, comfortable with market volatility, and want higher growth potential. You must be willing to actively monitor the policy and adjust premiums if needed.
                </p>
                <p className="text-sm text-gray-700 leading-relaxed mt-2">
                  <strong>Not sure?</strong> We can run personalized illustrations for both and show you exactly how each would perform based on your age, health, and financial goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indiana Estate Planning with Whole Life */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D97642]/20 rounded-full mb-4">
              <i className="ri-file-shield-line text-[#D97642]"></i>
              <span className="text-sm font-medium text-[#D97642]">Estate Planning</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Indiana Estate Planning with Whole Life Insurance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect your legacy, minimize taxes, and ensure your wealth passes to your heirs efficiently. Whole life insurance is a powerful estate planning tool for Indianapolis families.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-[#D97642]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-government-line text-3xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">No Indiana Inheritance Tax</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Good news: Indiana repealed its inheritance tax in 2013. Your beneficiaries receive the full death benefit without state inheritance tax deductions.
              </p>
              <div className="bg-[#F9F7F4] p-4 rounded-lg border border-gray-100">
                <div className="text-sm font-medium text-gray-900 mb-2">What This Means:</div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                    <span>$1M policy pays out full $1M to heirs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                    <span>No state tax on death benefits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                    <span>Simpler estate settlement process</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-[#1A3A52]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-bank-line text-3xl text-[#1A3A52]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Federal Estate Tax Planning</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                If your estate exceeds $13.61M (2024 limit), whole life insurance can provide liquidity to pay federal estate taxes without forcing heirs to sell assets.
              </p>
              <div className="bg-[#F9F7F4] p-4 rounded-lg border border-gray-100">
                <div className="text-sm font-medium text-gray-900 mb-2">Strategy: Irrevocable Life Insurance Trust (ILIT)</div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Place your whole life policy inside an ILIT. The death benefit passes to heirs <strong>outside</strong> your taxable estate, avoiding 40% federal estate tax.
                </p>
                <div className="mt-3 text-sm text-gray-700">
                  <strong>Example:</strong> $2M policy in ILIT saves heirs $800K in estate taxes.
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div className="w-14 h-14 bg-[#D97642]/10 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-3xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Medicaid Planning in Indiana</h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Whole life insurance cash value is <strong>exempt</strong> from Medicaid asset limits in Indiana if the face value is under $1,500. Larger policies require strategic planning.
              </p>
              <div className="bg-[#F9F7F4] p-4 rounded-lg border border-gray-100">
                <div className="text-sm font-medium text-gray-900 mb-2">Indiana Medicaid Rules:</div>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                    <span>Policies under $1,500 face value: fully exempt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                    <span>Larger policies: cash value counts toward $2,000 asset limit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-[#D97642] mt-0.5"></i>
                    <span>5-year lookback period for transfers</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Equalization Strategy for Heirs</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                If you plan to leave your business or farm to one child, use whole life insurance to provide equal inheritance to your other children—avoiding family conflict and resentment.
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mb-4">
                <div className="font-semibold text-gray-900 mb-3">Example Scenario:</div>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#D97642]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-[#D97642]">1</span>
                    </div>
                    <div>
                      <strong>Your Assets:</strong> $1.2M family farm + $300K in savings
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#D97642]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-[#D97642]">2</span>
                    </div>
                    <div>
                      <strong>Your Plan:</strong> Leave farm to son who works it, but daughter gets nothing?
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#D97642]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-[#D97642]">3</span>
                    </div>
                    <div>
                      <strong>Solution:</strong> Purchase $1.2M whole life policy naming daughter as beneficiary. Now both children receive equal $1.2M inheritance.
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 italic">
                This strategy is common among Indianapolis business owners and farmers in Hamilton, Hendricks, and Boone counties.
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Immediate Liquidity for Estate Settlement</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Death benefits are paid within 2-4 weeks, providing immediate cash to cover funeral costs, outstanding debts, estate taxes, and living expenses while the estate goes through probate.
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mb-4">
                <div className="font-semibold text-gray-900 mb-3">Common Estate Settlement Costs in Indiana:</div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Funeral &amp; burial expenses</span>
                    <span className="font-semibold text-gray-900">$8,000 - $15,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Outstanding medical bills</span>
                    <span className="font-semibold text-gray-900">$5,000 - $50,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Attorney &amp; probate fees</span>
                    <span className="font-semibold text-gray-900">$3,000 - $10,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Final income taxes</span>
                    <span className="font-semibold text-gray-900">$2,000 - $20,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Property maintenance (6-12 months)</span>
                    <span className="font-semibold text-gray-900">$10,000 - $30,000</span>
                  </div>
                  <div className="border-t-2 border-gray-300 pt-2 mt-2 flex justify-between items-center">
                    <span className="font-bold text-gray-900">Total Estimated Costs:</span>
                    <span className="font-bold text-[#D97642]">$28K - $125K</span>
                  </div>
                </div>
              </div>
              <div className="bg-[#F9F7F4] p-4 rounded-lg border border-gray-100">
                <p className="text-sm text-gray-700">
                  <strong>Without life insurance,</strong> heirs may be forced to sell property, liquidate retirement accounts, or take out loans to cover these costs. Whole life insurance provides immediate cash to avoid fire-sale situations.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F] p-8 rounded-2xl text-white text-center">
            <h3 className="text-2xl font-bold mb-3">Work with an Indiana Estate Planning Specialist</h3>
            <p className="text-white/80 mb-4 max-w-2xl mx-auto leading-relaxed">
              Estate planning with life insurance requires coordination between your insurance agent, attorney, and financial advisor. Our Indianapolis team works with local estate planning attorneys to ensure your whole life policy is properly structured for maximum tax efficiency and asset protection.
            </p>
            <button
              onClick={() => setShowBooking(true)}
              className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
            >
              Schedule Estate Planning Review
            </button>
          </div>
        </div>
      </section>

      {/* Real Cost Breakdown Table */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Real Cost Breakdown: Premium vs. Cash Value Growth</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See exactly how your premiums build cash value over time. These are real projections based on current whole life insurance rates for Indianapolis residents.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-200 shadow-sm">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-gray-900 mb-2">$250/month</div>
                <div className="text-gray-600">$500,000 Death Benefit</div>
                <div className="text-sm text-gray-500 mt-1">Age 35, Non-Smoker, Excellent Health</div>
              </div>
              <div className="space-y-4">
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 10</span>
                    <span className="text-xs text-gray-500">Total Paid: $30,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$21,500</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">72% of premiums paid</div>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 20</span>
                    <span className="text-xs text-gray-500">Total Paid: $60,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$58,000</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">97% of premiums paid</div>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 30</span>
                    <span className="text-xs text-gray-500">Total Paid: $90,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$112,000</span>
                  </div>
                  <div className="text-xs text-[#D97642] mt-1 font-medium">124% of premiums paid ✓</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border-2 border-gray-200 shadow-sm">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-gray-900 mb-2">$500/month</div>
                <div className="text-gray-600">$1,000,000 Death Benefit</div>
                <div className="text-sm text-gray-500 mt-1">Age 40, Non-Smoker, Excellent Health</div>
              </div>
              <div className="space-y-4">
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 10</span>
                    <span className="text-xs text-gray-500">Total Paid: $60,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$42,000</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">70% of premiums paid</div>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 20</span>
                    <span className="text-xs text-gray-500">Total Paid: $120,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$115,000</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">96% of premiums paid</div>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 30</span>
                    <span className="text-xs text-gray-500">Total Paid: $180,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$228,000</span>
                  </div>
                  <div className="text-xs text-[#D97642] mt-1 font-medium">125% of premiums paid ✓</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border-2 border-gray-200 shadow-sm">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-gray-900 mb-2">$1,000/month</div>
                <div className="text-gray-600">$2,000,000 Death Benefit</div>
                <div className="text-sm text-gray-500 mt-1">Age 45, Non-Smoker, Excellent Health</div>
              </div>
              <div className="space-y-4">
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 10</span>
                    <span className="text-xs text-gray-500">Total Paid: $120,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$82,000</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">68% of premiums paid</div>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 20</span>
                    <span className="text-xs text-gray-500">Total Paid: $240,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$228,000</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">95% of premiums paid</div>
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Year 30</span>
                    <span className="text-xs text-gray-500">Total Paid: $360,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Cash Value:</span>
                    <span className="text-lg font-bold text-[#D97642]">$445,000</span>
                  </div>
                  <div className="text-xs text-[#D97642] mt-1 font-medium">124% of premiums paid ✓</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#F9F7F4] p-8 rounded-2xl border border-gray-200 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Key Insights from These Projections</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#D97642]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-xl text-[#D97642]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Early Years: Building Phase</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    In the first 10 years, cash value grows slower (70-72% of premiums) because a larger portion covers insurance costs and policy setup. This is normal and expected.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-line-chart-line text-xl text-[#1A3A52]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Year 20: Break-Even Point</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    By year 20, your cash value nearly equals total premiums paid (95-97%). From this point forward, growth accelerates significantly due to compound interest.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#D97642]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-rocket-line text-xl text-[#D97642]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Year 30+: Wealth Accumulation</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    After 30 years, cash value exceeds premiums paid by 24-25%. This is when whole life becomes a powerful wealth-building tool, not just insurance.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-gift-line text-xl text-[#1A3A52]"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Dividends Boost Growth</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    These projections assume guaranteed rates only. Participating policies from mutual companies often pay dividends that can increase cash value by an additional 1-2% annually.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#F9F7F4] p-6 rounded-lg">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-2xl text-[#1A3A52] mt-1"></i>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Important Disclosure</h4>
                <p className="text-sm text-gray-700 leading-relaxed mb-2">
                  These projections are based on current whole life insurance rates and guaranteed growth rates from top-rated carriers. Actual cash values may vary based on:
                </p>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-[#1A3A52]">•</span>
                    <span>Your age, health, and smoking status</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#1A3A52]">•</span>
                    <span>The specific insurance carrier and policy type</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#1A3A52]">•</span>
                    <span>Dividend payments (not guaranteed but historically consistent)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#1A3A52]">•</span>
                    <span>Any policy loans or withdrawals you take</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-700 leading-relaxed mt-3">
                  <strong>We provide personalized illustrations</strong> showing your exact cash value projections based on your specific situation. Schedule a consultation to see your real numbers.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => setShowBooking(true)}
              className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Get Your Personalized Illustration
            </button>
            <p className="text-sm text-gray-600 mt-3">See exactly how much cash value you'll build based on your age and health</p>
          </div>
        </div>
      </section>

      {/* Whole Life Calculator Section */}
      <WholeLifeCalculator />

      {/* Explore More Coverage Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Explore More Coverage Options</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whole life insurance is just one part of a comprehensive financial protection strategy. Explore other coverage options that may fit your needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <a href="/term-life" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-[#D97642]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D97642] transition-colors">
                <i className="ri-calendar-check-line text-3xl text-[#D97642] group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#D97642] transition-colors">Term Life Insurance</h3>
              <p className="text-gray-600 leading-relaxed mb-4">Affordable coverage for 10-30 years. Perfect for income replacement and mortgage protection.</p>
              <div className="flex items-center gap-2 text-[#D97642] font-semibold">
                <span>Learn More</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>

            <a href="/final-expense" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-[#1A3A52]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1A3A52] transition-colors">
                <i className="ri-hand-heart-line text-3xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1A3A52] transition-colors">Final Expense Insurance</h3>
              <p className="text-gray-600 leading-relaxed mb-4">Cover funeral costs and final expenses. No medical exam required for most applicants.</p>
              <div className="flex items-center gap-2 text-[#1A3A52] font-semibold">
                <span>Learn More</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>

            <a href="/mortgage-protection" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-[#D97642]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D97642] transition-colors">
                <i className="ri-home-heart-line text-3xl text-[#D97642] group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#D97642] transition-colors">Mortgage Protection</h3>
              <p className="text-gray-600 leading-relaxed mb-4">Ensure your family keeps the home if something happens to you. Coverage matches your mortgage balance.</p>
              <div className="flex items-center gap-2 text-[#D97642] font-semibold">
                <span>Learn More</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>

            <a href="/annuities" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100 group">
              <div className="w-14 h-14 bg-[#1A3A52]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1A3A52] transition-colors">
                <i className="ri-line-chart-line text-3xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#1A3A52] transition-colors">Annuities</h3>
              <p className="text-gray-600 leading-relaxed mb-4">Guaranteed retirement income for life. Protect your savings and create a pension-like income stream.</p>
              <div className="flex items-center gap-2 text-[#1A3A52] font-semibold">
                <span>Learn More</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>

          <div className="mt-12 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Related Wealth-Building Strategies</h3>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <a href="/generational-wealth" className="bg-[#F9F7F4] p-6 rounded-xl hover:shadow-md transition-all border border-gray-100">
                <h4 className="text-lg font-bold text-[#1A3A52] mb-2">Generational Wealth Planning</h4>
                <p className="text-sm text-gray-600 mb-3">Build generational wealth for your family and leave a lasting legacy</p>
                <span className="text-[#D97642] font-semibold text-sm">Explore Strategy →</span>
              </a>

              <a href="/wealth-building" className="bg-[#F9F7F4] p-6 rounded-xl hover:shadow-md transition-all border border-gray-100">
                <h4 className="text-lg font-bold text-[#1A3A52] mb-2">6-Step Wealth-Building System</h4>
                <p className="text-sm text-gray-600 mb-3">Our proven blueprint for tax-free retirement income</p>
                <span className="text-[#D97642] font-semibold text-sm">Learn More →</span>
              </a>

              <a href="/term-vs-whole-life" className="bg-[#F9F7F4] p-6 rounded-xl hover:shadow-md transition-all border border-gray-100">
                <h4 className="text-lg font-bold text-[#1A3A52] mb-2">Term vs. Whole Life Comparison</h4>
                <p className="text-sm text-gray-600 mb-3">Which builds more wealth for your family?</p>
                <span className="text-[#D97642] font-semibold text-sm">Compare Now →</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="why-choose-heading" className="text-4xl font-bold text-gray-900 mb-4">Why Choose Whole Life Insurance?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whole life insurance offers unique benefits that term life and other investments can't match.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-infinity-line',
                title: 'Lifetime Coverage',
                description: 'Your policy never expires. Coverage lasts until age 100 or beyond, with guaranteed premiums that never increase.',
                accent: true
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Guaranteed Cash Value',
                description: 'Build tax-deferred wealth with guaranteed growth rates. Your cash value is contractually guaranteed to reach specific amounts.',
                accent: false
              },
              {
                icon: 'ri-lock-line',
                title: 'Fixed Premiums',
                description: 'Your premium is locked in for life. No surprises, no increases—just predictable payments you can budget for.',
                accent: true
              },
              {
                icon: 'ri-hand-coin-line',
                title: 'Borrow Against Cash Value',
                description: 'Access your cash value through low-interest policy loans. No credit check, no approval process, no repayment schedule.',
                accent: false
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Tax-Free Death Benefit',
                description: 'Your beneficiaries receive the full death benefit income tax-free. No federal or Indiana state taxes on the payout.',
                accent: true
              },
              {
                icon: 'ri-gift-line',
                title: 'Dividend Payments',
                description: 'Participating policies from mutual companies pay annual dividends that can increase your cash value or reduce premiums.',
                accent: false
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-100">
                <div className={`w-14 h-14 ${benefit.accent ? 'bg-[#D97642]/10' : 'bg-[#1A3A52]/10'} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${benefit.icon} text-3xl ${benefit.accent ? 'text-[#D97642]' : 'text-[#1A3A52]'}`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Build Wealth While Protecting Your Family?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Get a personalized whole life insurance quote in minutes. Our Indianapolis team will show you exactly how much coverage you can afford and how much cash value you'll build.
          </p>
          <button
            onClick={() => setShowBooking(true)}
            className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg hover:shadow-xl text-lg whitespace-nowrap"
          >
            Get Your Free Quote
          </button>
        </div>
      </section>

      {/* Serving Community Section */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D97642]/10 rounded-full mb-4">
              <i className="ri-article-line text-[#D97642]"></i>
              <span className="text-sm font-medium text-[#D97642]">Continue Learning</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Related Articles &amp; Resources</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Deepen your understanding of whole life insurance and wealth-building strategies with these guides from our Indiana specialists.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Blog: Whole Life Insurance */}
            <a href="/blog/whole-life-insurance-indianapolis" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-shield-check-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Whole Life Insurance in Indianapolis: Complete Guide</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Everything Indianapolis families need to know about permanent coverage, cash value, and dividends.</p>
                </div>
              </div>
            </a>

            {/* Blog: Term vs Whole Life */}
            <a href="/blog/term-vs-whole-life-indianapolis" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-scales-3-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Term vs. Whole Life Insurance in Indianapolis</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Side-by-side comparison with real numbers — which policy builds more wealth for your family?</p>
                </div>
              </div>
            </a>

            {/* Blog: Wealth Building with Life Insurance */}
            <a href="/blog/wealth-building-life-insurance-indianapolis" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0 group-hover:bg-[#D97642] transition-colors">
                  <i className="ri-line-chart-line text-xl text-[#D97642] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#D97642] uppercase tracking-wider">Blog</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Building Wealth with Life Insurance in Indianapolis</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">How Indianapolis families use whole life and IUL policies as tax-free wealth-building engines.</p>
                </div>
              </div>
            </a>

            {/* Blog: Indexed Universal Life */}
            <a href="/blog/indexed-universal-life-indianapolis" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                  <i className="ri-stock-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Blog</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Indexed Universal Life Insurance (IUL) in Indianapolis</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Market-linked growth with a 0% floor — is IUL the right alternative to whole life for you?</p>
                </div>
              </div>
            </a>

            {/* Resource: Term vs Whole for Homeowners */}
            <a href="/resources/term-vs-whole-life-indianapolis-homeowners" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                  <i className="ri-home-4-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Resource Guide</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">Term vs. Whole Life for Indianapolis Homeowners</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Which policy type is right for your mortgage? A complete homeowner's decision framework.</p>
                </div>
              </div>
            </a>

            {/* Resource: Life Insurance Mistakes */}
            <a href="/resources/life-insurance-mistakes-indiana-families" className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1A3A52]/10 rounded-lg flex-shrink-0 group-hover:bg-[#1A3A52] transition-colors">
                  <i className="ri-alert-line text-xl text-[#1A3A52] group-hover:text-white transition-colors"></i>
                </div>
                <div>
                  <span className="text-xs font-semibold text-[#1A3A52] uppercase tracking-wider">Resource Guide</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2 leading-snug group-hover:text-[#D97642] transition-colors">7 Life Insurance Mistakes Indiana Families Make</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Avoid the coverage errors that leave Indiana families underinsured or overpaying every month.</p>
                </div>
              </div>
            </a>
          </div>

          <div className="mt-10 bg-gradient-to-r from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-3">Ready to See Your Whole Life Illustration?</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">Our licensed Indiana agents run personalized cash value projections from 20+ carriers — so you see exactly what you'll build before you commit.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowBooking(true)}
                className="px-8 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap"
              >
                Get My Free Illustration
              </button>
              <a href="tel:+13176482983" className="px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap inline-flex items-center justify-center gap-2">
                <i className="ri-phone-line"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      <ServingCommunitySection productName="Whole Life Insurance" productSlug="whole-life" />

      <Footer />
      <MobileProductCTABar productName="Whole Life Insurance" quoteHref="/instant-quote" />
      {/* Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-2xl font-bold text-gray-900">Schedule Your Consultation</h3>
              <button
                onClick={() => setShowBooking(false)}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>
            <div className="p-6">
              <BookingCalendar
                timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                texts={{
                  heading: 'Book a Consultation',
                  step0Title: 'Select a Date',
                  step1Title: 'Select a Time',
                  step2Title: 'Review & Book',
                  selectDate: 'Select Date',
                  selectTime: 'Select Time',
                  reviewAndBook: 'Review & Book',
                  previousMonth: 'Previous Month',
                  nextMonth: 'Next Month',
                  noSlotsAvailable: 'No slots available',
                  selectedDateTime: 'Selected Date & Time',
                  modifyTime: 'Modify',
                  customerName: 'Your Name',
                  customerPhone: 'Phone Number',
                  notes: 'Notes',
                  submitButton: 'Confirm Booking',
                  submitting: 'Submitting...',
                  successMessage: 'Booking confirmed! We\'ll be in touch shortly.',
                  errorMessage: 'Something went wrong. Please try again.',
                  backButton: 'Back',
                  required: '*',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
